import { useCallback, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';

export type EventDragMode = 'move' | 'resize';

export interface EventDragCommit {
  /** Minute offset from the gesture's origin (positive = later). */
  readonly minutesDelta: number;
  /** Day key under the pointer at release (`YYYY-MM-DD`). `null` when
   *  no `[data-day-key]` ancestor was found — caller should treat as
   *  same-day (Day view) or no-op. */
  readonly targetDayKey: string | null;
  readonly mode: EventDragMode;
}

export interface EventDragVisual {
  readonly active: boolean;
  /** Px offset to apply to the dragged block (so it floats with the
   *  pointer without affecting layout). */
  readonly translateY: number;
  /** Px offset across columns. Always 0 in Day view; in Week view this
   *  is the column-width delta times the number of columns crossed. */
  readonly translateX: number;
  /** When resizing, additional height in px (so the bottom edge tracks
   *  the pointer). */
  readonly extraHeight: number;
  /** Live target day key (updated each pointer move) so views can
   *  highlight the destination column. */
  readonly targetDayKey: string | null;
}

export interface UseEventDragOptions {
  readonly hourHeight: number;
  readonly snapMinutes?: number;
  /** Origin day key — used to compute cross-column horizontal offset. */
  readonly originDayKey: string;
  /** Mode for the in-flight gesture. */
  readonly mode: EventDragMode;
  /** Fired on pointer up with the resolved delta. Caller computes the
   *  new startsAt / endsAt from this. */
  readonly onCommit: (commit: EventDragCommit) => void;
  /** Drag must travel this many px before the gesture engages — keeps
   *  bare clicks from triggering a no-op move. Default 4. */
  readonly thresholdPx?: number;
  readonly disabled?: boolean;
  /** When true, touch-pointer gestures engage drag. Default false: on
   *  phones, touch on an event is "tap to open the detail sheet"; the
   *  user expects long-press for system menus and finger-scroll for
   *  the page, NOT a 4-px-snap reschedule. Mouse + pen still work. */
  readonly enableTouch?: boolean;
}

export interface UseEventDragResult {
  readonly visual: EventDragVisual;
  readonly bind: {
    readonly onPointerDown: (e: ReactPointerEvent<HTMLElement>) => void;
  };
  /** True while a gesture is in progress (passed pointer travel
   *  threshold). Useful for suppressing the click that would otherwise
   *  fire on pointer up after a real drag. */
  readonly suppressNextClick: () => boolean;
}

const DEFAULT_SNAP = 15;
const DEFAULT_THRESHOLD = 4;

/**
 * Pointer-events drag hook for timed-grid event blocks.
 *
 * Two modes:
 * - `move`   → tracks both vertical (time) and horizontal (cross-column)
 *              motion. On commit, fires `{ minutesDelta, targetDayKey }`.
 * - `resize` → tracks only vertical motion. On commit, fires
 *              `{ minutesDelta, targetDayKey: originDayKey }`.
 *
 * Day-key detection on cross-column drag uses `elementFromPoint` to
 * find an ancestor with `[data-day-key]`. The view is responsible for
 * marking each column with that attribute.
 *
 * The hook calls `setPointerCapture` so the gesture survives the
 * pointer leaving the block, and it suppresses the next click on the
 * block (so commit doesn't immediately re-open the detail sheet).
 */
export function useEventDrag(options: UseEventDragOptions): UseEventDragResult {
  const {
    hourHeight,
    snapMinutes = DEFAULT_SNAP,
    originDayKey,
    mode,
    onCommit,
    thresholdPx = DEFAULT_THRESHOLD,
    disabled = false,
    enableTouch = false,
  } = options;

  const [visual, setVisual] = useState<EventDragVisual>({
    active: false,
    translateY: 0,
    translateX: 0,
    extraHeight: 0,
    targetDayKey: null,
  });

  // Refs to keep the active gesture's bookkeeping out of state churn.
  const startRef = useRef<{
    x: number;
    y: number;
    columnWidthPx: number;
    engaged: boolean;
    suppressClick: boolean;
    /** Last snap step we committed to React state — used to bail out
     *  of `setVisual` on raw pointer moves that haven't crossed a
     *  snap-boundary yet. Without this guard, React re-renders
     *  ~120 times/sec on macOS trackpads, which produces a visible
     *  flicker on the dragged block. */
    lastSnapMinutes: number;
    lastDayKey: string;
  } | null>(null);

  const suppressNextClick = useCallback((): boolean => {
    const v = startRef.current?.suppressClick ?? false;
    if (startRef.current !== null) startRef.current.suppressClick = false;
    return v;
  }, []);

  /**
   * Resolve the column the pointer is over right now.
   *
   * Why this is non-trivial: the dragged block is a DOM child of its
   * *origin* column, but we visually `transform: translate` it across
   * neighbouring columns. A naive `elementFromPoint` would hit the
   * block (z:12 while dragging) and `closest('[data-day-key]')` would
   * then walk up to the *origin* column — so `liveDayKey` would oscillate
   * between origin and target each frame, producing the flicker the
   * user sees on cross-day drags.
   *
   * The fix: use `elementsFromPoint` (returns the full z-ordered hit
   * stack) and skip the dragged block + any descendant of it. The next
   * matching `[data-day-key]` ancestor is the column actually under the
   * cursor.
   */
  const findDayKey = (
    clientX: number,
    clientY: number,
    dragEl: HTMLElement | null,
  ): string | null => {
    if (typeof document === 'undefined') return null;
    // Prefer elementsFromPoint when available (Safari ≥ 11). Fall back
    // to elementFromPoint with origin-column rescue for ancient envs.
    const stack: readonly Element[] =
      typeof document.elementsFromPoint === 'function'
        ? document.elementsFromPoint(clientX, clientY)
        : (() => {
            const el = document.elementFromPoint(clientX, clientY);
            return el === null ? [] : [el];
          })();
    for (const el of stack) {
      // Skip the dragged block itself and anything inside it. Without
      // this, the dragged block (translated over the destination column)
      // shadows the column we want to detect.
      if (dragEl !== null && dragEl.contains(el)) continue;
      const target = (el).closest('[data-day-key]');
      if (target === null) continue;
      // Sanity: if the matched column happens to be a parent of the
      // dragged block (origin column reached *not* through the block
      // itself — rare), accept it; that means the cursor really is
      // back over the origin column. We only want to skip the block.
      return (target as HTMLElement).dataset.dayKey ?? null;
    }
    return null;
  };

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLElement>): void => {
      if (disabled) return;
      // Only react to primary button.
      if (e.button !== 0) return;
      // Bail on touch unless the consumer opts in. On mobile, drag
      // gestures conflict with tap-to-open and page scroll; the FAB +
      // composer handles event creation, and mobile views show events
      // as tappable cards rather than draggable timed blocks.
      if (e.pointerType === 'touch' && !enableTouch) return;
      // Find the column under origin to derive column width for x-translate.
      const column =
        typeof document === 'undefined'
          ? null
          : (document.elementFromPoint(e.clientX, e.clientY))?.closest(
              '[data-day-key]',
            );
      const columnWidthPx =
        column !== null && column !== undefined
          ? (column as HTMLElement).getBoundingClientRect().width
          : 0;
      const dragEl = e.currentTarget;
      startRef.current = {
        x: e.clientX,
        y: e.clientY,
        columnWidthPx,
        engaged: false,
        suppressClick: false,
        lastSnapMinutes: 0,
        lastDayKey: originDayKey,
      };
      try {
        dragEl.setPointerCapture(e.pointerId);
      } catch {
        // Some test environments / older Safari refuse capture on
        // synthetic events; the gesture still works without capture.
      }

      const handleMove = (ev: PointerEvent): void => {
        const start = startRef.current;
        if (start === null) return;
        const dx = ev.clientX - start.x;
        const dy = ev.clientY - start.y;
        if (!start.engaged && Math.hypot(dx, dy) < thresholdPx) return;
        start.engaged = true;
        start.suppressClick = true;

        const minutesPerPx = 60 / hourHeight;
        const rawMinutes = dy * minutesPerPx;
        const snapped = Math.round(rawMinutes / snapMinutes) * snapMinutes;
        const liveDayKey =
          mode === 'move'
            ? findDayKey(ev.clientX, ev.clientY, dragEl) ?? originDayKey
            : originDayKey;

        // Bail when the snap step + target column haven't changed since
        // the last commit. This collapses ~120 raw pointer events per
        // second down to one re-render per snap boundary crossed.
        if (
          start.lastSnapMinutes === snapped &&
          start.lastDayKey === liveDayKey &&
          start.engaged
        ) {
          return;
        }
        start.lastSnapMinutes = snapped;
        start.lastDayKey = liveDayKey;

        // For cross-column move, snap the horizontal position to the
        // target column's left edge so the block visually clicks into
        // place even when the pointer is mid-column.
        let translateX = 0;
        if (mode === 'move' && liveDayKey !== originDayKey && start.columnWidthPx > 0) {
          if (typeof document !== 'undefined') {
            const target = document.querySelector(
              `[data-day-key="${cssEscape(liveDayKey)}"]`,
            );
            const origin = document.querySelector(
              `[data-day-key="${cssEscape(originDayKey)}"]`,
            );
            if (target !== null && origin !== null) {
              translateX =
                target.getBoundingClientRect().left - origin.getBoundingClientRect().left;
            }
          }
        }

        const translateY = mode === 'move' ? (snapped / 60) * hourHeight : 0;
        const extraHeight = mode === 'resize' ? (snapped / 60) * hourHeight : 0;

        setVisual({
          active: true,
          translateY,
          translateX,
          extraHeight,
          targetDayKey: liveDayKey,
        });
      };

      const handleUp = (ev: PointerEvent): void => {
        const start = startRef.current;
        document.removeEventListener('pointermove', handleMove);
        document.removeEventListener('pointerup', handleUp);
        document.removeEventListener('pointercancel', handleUp);
        if (start === null) return;

        const dy = ev.clientY - start.y;
        const minutesPerPx = 60 / hourHeight;
        const rawMinutes = dy * minutesPerPx;
        const snapped = Math.round(rawMinutes / snapMinutes) * snapMinutes;
        const targetDayKey =
          mode === 'move'
            ? findDayKey(ev.clientX, ev.clientY, dragEl) ?? originDayKey
            : originDayKey;

        setVisual({
          active: false,
          translateY: 0,
          translateX: 0,
          extraHeight: 0,
          targetDayKey: null,
        });

        if (start.engaged) {
          onCommit({
            minutesDelta: snapped,
            targetDayKey,
            mode,
          });
        }
        // Intentionally NOT clearing startRef.current here — the click
        // event fires synchronously after pointerup and reads
        // `startRef.current.suppressClick` to decide whether to swallow
        // itself. Nulling now would re-open the detail sheet on every
        // drag commit. The next pointerdown overwrites the ref, so
        // there's no leak.
      };

      document.addEventListener('pointermove', handleMove);
      document.addEventListener('pointerup', handleUp);
      document.addEventListener('pointercancel', handleUp);
    },
    [disabled, hourHeight, snapMinutes, originDayKey, mode, onCommit, thresholdPx, enableTouch],
  );

  return {
    visual,
    bind: { onPointerDown },
    suppressNextClick,
  };
}

/** Minimal CSS.escape polyfill so day-key attribute selectors work for
 *  any string. We can't rely on `CSS.escape` in non-DOM Vitest, and our
 *  day keys are always `YYYY-MM-DD` so the only concern is that the
 *  embedded hyphens don't trip selector parsing — they don't, but we
 *  still escape just to be safe against future free-form keys. */
function cssEscape(s: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(s);
  }
  return s.replace(/([^a-zA-Z0-9_-])/g, '\\$1');
}
